import React from 'react'

import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import { Icon } from '@mxenabled/mx-icons'

import { Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../common'

import { HeaderCell } from './HeaderCell'

interface HoldingDetail {
  id: string
  description: string
  qty: number
  holding: string
  costBasis: number
  rawCostBasis: number
  totalGainLoss: number
  gainLossAvailable: boolean
  gainLossBasis: number
  marketValue: number
}

interface HoldingsTableProps {
  data: HoldingDetail[]
  editingHoldingId: string | null
  currentEditedCostBasis: string
  handleCostBasisClick: (holdingId: string, currentCostBasis: number) => void
  handleCostBasisChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleCostBasisKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, id: string) => void
  handleCostBasisBlur: (id: string) => void
}

const HoldingsTable: React.FC<HoldingsTableProps> = ({
  data,
  editingHoldingId,
  currentEditedCostBasis,
  handleCostBasisClick,
  handleCostBasisChange,
  handleCostBasisKeyDown,
  handleCostBasisBlur,
}) => {
  const { investments: copy } = useGlobalCopyStore()
  const theme = useTheme()

  const isDarkMode = theme.palette.mode === 'dark'
  const gainDisplayColor = 'success.main'
  const lossDisplayColor = isDarkMode ? 'grey.300' : 'grey.700'

  const gainLossDisplayColor = (value: number, type: string = 'lesserThan') => {
    if (type === 'greaterThan') {
      return value >= 0 ? gainDisplayColor : lossDisplayColor
    }
    return value <= 0 ? lossDisplayColor : gainDisplayColor
  }

  const columns: GridColDef[] = [
    {
      field: 'qty',
      headerName: copy.overview.qty_header,
      sortable: false,
      width: 48,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'holding',
      headerName: copy.overview.holding_header,
      flex: 1,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Stack sx={{ alignItems: 'center', gap: 16, flexDirection: 'row' }}>
          <Stack>
            {/* TODO :InstitutionLogo in future */}
            <Text sx={{ fontWeight: 700 }}>{params.row.holding}</Text>
            <Text sx={{ color: 'text.secondary', fontWeight: 400 }} variant="tiny">
              {params.row.description}
            </Text>
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'costBasis',
      headerName: copy.overview.cost_basis_header,
      width: 132,
      renderHeader: HeaderCell,
      renderCell: (params) => {
        const isCurrentlyEditing = editingHoldingId === params.row.id
        return (
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
            {isCurrentlyEditing ? (
              <TextField
                autoFocus={true}
                onBlur={() => handleCostBasisBlur(params.row.id)}
                onChange={handleCostBasisChange}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  handleCostBasisKeyDown(e, params.row.id)
                }
                size="small"
                sx={{
                  fontSize: 14,
                  width: 57,
                  '& .MuiInputBase-input': {
                    padding: '4px 0',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'text.primary',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
                value={currentEditedCostBasis}
              />
            ) : (
              <Text sx={{ fontSize: 14, width: 57 }} variant="body2">
                {params.row.gainLossAvailable
                  ? params.value?.toLocaleString('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    })
                  : '—'}
              </Text>
            )}
            <IconButton
              onClick={() => handleCostBasisClick(params.row.id, params.row.rawCostBasis)}
              sx={{ minWidth: 12, minHeight: 12 }}
            >
              <Icon name={'stylus'} size={12} sx={{ color: 'grey.700' }} />
            </IconButton>
          </Stack>
        )
      },
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'totalGainLoss',
      headerName: copy.overview.total_gain_loss_header,
      width: 196,
      renderHeader: HeaderCell,
      renderCell: (params) => {
        const gainLossBasis = params.row.gainLossBasis
        const gainLossPercentage = gainLossBasis
          ? (params.row.totalGainLoss / gainLossBasis) * 100
          : 0
        // No known cost basis — show "—" instead of a fabricated 0/100%.
        if (!params.row.gainLossAvailable) {
          return (
            <Text sx={{ color: 'text.secondary' }} variant="body2">
              —
            </Text>
          )
        }
        return (
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 2 }}>
            {params.value !== 0 && (
              <Icon
                name={params.value > 0 ? 'trending_up' : 'trending_down'}
                size={16}
                sx={{
                  color: gainLossDisplayColor(params.value),
                  mr: 1,
                  verticalAlign: 'middle',
                }}
              />
            )}
            <Text
              sx={{
                color: gainLossDisplayColor(params.value),
                fontWeight: params.value > 0 ? 700 : 400,
              }}
              variant="body2"
            >
              {gainLossBasis ? `${gainLossPercentage.toFixed(2)}%` : '0.00%'}
            </Text>
            <Text sx={{ color: 'text.secondary' }} variant="caption">
              |
            </Text>
            <Text
              sx={{
                color: gainLossDisplayColor(params.value),
                fontWeight: params.value > 0 ? 700 : 400,
              }}
              variant="body2"
            >
              {Math.abs(params.value).toLocaleString('en-US', {
                currency: 'USD',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                style: 'currency',
              })}
            </Text>
          </Stack>
        )
      },
      align: 'right',
      headerAlign: 'right',
      sortable: false,
    },
    {
      field: 'marketValue',
      headerName: copy.overview.market_value_header,
      width: 132,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Text sx={{ fontWeight: 700 }} variant={'body2'}>
          {params.value?.toLocaleString('en-US', {
            currency: 'USD',
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: 'currency',
          })}
        </Text>
      ),
      align: 'right',
      headerAlign: 'right',
    },
  ]

  return (
    <Paper
      sx={{
        boxShadow: 'none',
        width: '100%',
      }}
    >
      <DataGridPro
        columns={columns}
        disableColumnFilter={true}
        disableColumnMenu={true}
        disableRowSelectionOnClick={true}
        hideFooter={true}
        rows={data}
        slotProps={{ baseIconButton: { sx: { display: 'none' } } }}
      />
    </Paper>
  )
}

export default HoldingsTable
